import { useCallback, useEffect, useRef, useState } from 'react';
import { TOAST_MS } from '../components/ui/Toast';

/**
 * The "Task completed · Undo" toast state of the Task Board (P-TASK-062),
 * shared with the unit Activity tab (D11). One toast at a time: a new offer
 * replaces the current one (fresh key, so screen readers announce it and the
 * timer restarts). It times out after TOAST_MS on its own and never takes
 * focus (the caller renders it in a polite live region). `undo` runs the
 * offered action once and closes the toast.
 */
export default function useUndoToast(duration = TOAST_MS) {
    const [toast, setToast] = useState(null); // { key, text, onUndo }
    const seq = useRef(0);
    const current = useRef(null);
    current.current = toast;

    const offer = useCallback(({ text, onUndo }) => {
        seq.current += 1;
        setToast({ key: seq.current, text, onUndo });
    }, []);

    const dismiss = useCallback(() => setToast(null), []);

    const undo = useCallback(() => {
        const t = current.current;
        if (!t) return;
        current.current = null;
        setToast(null);
        t.onUndo?.();
    }, []);

    useEffect(() => {
        if (!toast) return undefined;
        const timer = setTimeout(() => setToast(null), duration);
        return () => clearTimeout(timer);
    }, [toast, duration]);

    return { toast, offer, dismiss, undo };
}
